import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Subscription } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";
import { SpinnerDialogComponent } from "src/app/shared/components/spinner-dialog/spinner-dialog.component";
import { MessageService } from "src/app/shared/services/message.service";
import { UIState } from "src/app/shared/state/ui.state";
import { VlokExemption } from "../shared/models/vlok.model";
import { MagdaService } from "../shared/services/magda.service";

@Component({
  selector: "app-vlok-exemption-list",
  templateUrl: "./vlok-exemption-list.component.html",
})
export class VlokExemptionListComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  loading = true;
  spinnerDialogRef: MatDialogRef<SpinnerDialogComponent>;

  displayedColumns: string[] = ["PaCode", "EnCode", "BuildingCode", "Address", "Reason", "Actions"];

  data: VlokExemption[] = [];
  dataSource: MatTableDataSource<VlokExemption> = new MatTableDataSource<VlokExemption>([]);
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private magdaService: MagdaService,
    private uiState: UIState,
    private messageService: MessageService,
    private matDialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.uiState.breadcrumbs$.next([
      {
        name: "VLOK Uitzonderingen",
        routerLink: "/vlok-uitzonderingen",
      }
    ]);

    this.reload();
  }

  reload(): void {
    this.subscriptions.push(this.magdaService.getVlokExemptionList()
      .pipe(
        take(1),
        tap((definitions: VlokExemption[]) => {
          this.data = definitions;

          this.dataSource = new MatTableDataSource(this.data);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;

          this.closeSpinner();
          this.loading = false;
        })
      )
      .subscribe()
    );
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  deleteItem(item: VlokExemption): void {
    if (!item) { 
      return;
    }

    this.messageService.openConfirmationDialog(
      "Uitzondering verwijderen",
      "Weet je zeker dat je deze uitzondering wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt."
    ).pipe(
      take(1),
      filter(r => r),
      tap(() => this.openSpinner("Bezig met verwijderen van de uitzondering...")),
      switchMap(() => this.magdaService.deleteVlokExemption(item.Id)),
      tap(() => this.messageService.showSnackBarInfo("Uitzondering is verwijderd")),
      tap(() => this.reload())
    ).subscribe();
  }

  resyncItem(item: VlokExemption): void {
    if (!item) { 
      return;
    }

    this.openSpinner("Bezig met synchroniseren van de uitzondering...");
  
    this.subscriptions.push(this.magdaService.resyncVlokExemption(item.Id)
      .pipe(
        take(1),
        tap(response => {
          if (response.Success) {
            this.messageService.showSnackBarInfo("Uitzondering is opnieuw gesynchroniseerd")
          }
        }),
        tap(() => this.reload()),
      )
      .subscribe()
    );
  }

  resyncAll(): void {
    this.openSpinner("Bezig met checken van VLOK uitzonderingen...");

    this.subscriptions.push(this.magdaService.resyncVlokExemptions()
      .pipe(
        take(1),
        tap(() => this.messageService.showSnackBarInfo("VLOK uitzonderingen zijn opnieuw gesynchroniseerd")),
        tap(() => this.reload())
      )
      .subscribe()
    );
  }

  openSpinner(message: string): void {
    this.spinnerDialogRef = this.matDialog.open(SpinnerDialogComponent, {
      width: "500px",
      disableClose: true,
      data: {
        title: message,
      },
    });
  }

  closeSpinner(): void {
    if (!this.spinnerDialogRef) {
      return;
    }

    this.spinnerDialogRef.close();
    this.spinnerDialogRef = null;
  }

  openUrl(url: string): void {
    window.open(url, "_blank");
  }
}
